import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getOrderStatistics } from "../../redux/actions/orderActions";
import DashboardLayout from "./DashboardLayout";
import { useNavigate } from "react-router-dom";
import { fetchDashboardData } from "../../redux/actions/dashboardActions";
import { fetchStateCityStats } from "../../redux/actions/orderActions";
import { useLocation } from "react-router-dom";
import DashboardCard from "../../Pages/Components/DashboardCard";
import SalesmanDashboard from "../SalesMan/SalesmanDashboard";
import { fetchSalesmanDashboardData } from "../../redux/actions/salesmanAction";
import CustomerDashboard from "../Customer/CustomerDashboard";
import { FullScreenLoader } from "../Components/Loader";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
} from "recharts";
import {
  FiDollarSign,
  FiTag,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiUserCheck,
  FiLayers,
} from "react-icons/fi";

const USER_TYPE_TO_ORDER_TYPE = {
  loyality: "loyality",
  promotional: "promotional",
  cashback: "cashback",
};

const ORDER_TYPE_LABELS = {
  loyality: "Loyalty Coupon",
  promotional: "Promotional",
  cashback: "Cashback",
};

const OrderStatsDashboard = () => {
  const getOrderTypeLabel = (type) => ORDER_TYPE_LABELS[type] || type;

  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);
  const roleId = useSelector((state) => state.dashboard?.data?.role_id);
  const userId = useSelector((state) => state.dashboard?.data?.user_id);

  const navigate = useNavigate();
  const location = useLocation();

  const {
    loading = false,
    data: stats = [],
    overall_totals = {},
    error = null,
  } = useSelector((state) => state.orderStatistics || {});

  const {
    loading: salesmanLoading,
    dashboardData: salesmanDashboardData,
    error: salesmanError,
  } = useSelector((state) => state.salesman || {});

  const initialViewType = location.state?.viewType || "state";
  const [viewType, setViewType] = useState(initialViewType);

  const userType = userInfo?.user_type;
  const derivedOrderType = USER_TYPE_TO_ORDER_TYPE[userType] || null;
  const isMultiTypeRole = roleId === "6";

  const [selectedOrderType, setSelectedOrderType] = useState(derivedOrderType);

  const cityStats = useSelector(
    (state) => state.stateCityStats?.data?.city_stats || [],
  );
  const stateStats = useSelector(
    (state) => state.stateCityStats?.data?.state_stats || [],
  );

  const COLORS = ["#004D99", "#34D379", "#FBBF24", "#818CF8"];

  const availableOrderTypes = isMultiTypeRole
    ? [...new Set(stats.map((s) => s.order_type))]
    : derivedOrderType
      ? [derivedOrderType]
      : [...new Set(stats.map((s) => s.order_type))];

  useEffect(() => {
    if (isMultiTypeRole) {
      if (stats.length > 0 && !selectedOrderType) {
        setSelectedOrderType(stats[0].order_type);
      }
    } else if (derivedOrderType) {
      setSelectedOrderType(derivedOrderType);
    } else if (stats.length > 0 && !selectedOrderType) {
      setSelectedOrderType(stats[0].order_type);
    }
  }, [stats, derivedOrderType, roleId]);

  // Filter stats to selected order type
  const filteredStats = stats.filter(
    (order) => order.order_type === selectedOrderType,
  );

  // Use overall_totals from API response directly
  const totals = {
    overall_amount: overall_totals.overall_amount || 0,
    overall_quantity: overall_totals.overall_quantity || 0,
    overall_active: overall_totals.overall_active || 0,
    overall_inactive: overall_totals.overall_inactive || 0,
    overall_used: overall_totals.overall_used || 0,
    overall_expired: overall_totals.overall_expired || 0,
    overall_schemes: overall_totals.overall_schemes || 0,
    overall_pending: overall_totals.overall_pending || 0,
    overall_accumulated: overall_totals.overall_accumulated || 0,
    overall_redeemed: overall_totals.overall_redeemed || 0,
  };

  const pieData = [
    { name: "Active", value: totals.overall_active },
    { name: "Inactive", value: totals.overall_inactive },
    { name: "Used QR", value: totals.overall_used },
    { name: "Expired", value: totals.overall_expired },
  ];

  useEffect(() => {
    if (!userInfo?.access_token) {
      navigate("/login");
    } else {
      dispatch(fetchDashboardData(userInfo.access_token));
      if (roleId === "2" && userId) {
        dispatch(
          fetchSalesmanDashboardData(
            { salesman_id: userId },
            userInfo.access_token,
          ),
        );
      } else if (roleId !== "2") {
        dispatch(getOrderStatistics(userInfo.access_token, userType));
        dispatch(fetchStateCityStats(userInfo.access_token));
      }
    }
  }, [dispatch, userInfo, navigate, roleId, userId]);

  if (roleId === "2") {
    return (
      <DashboardLayout>
        <FullScreenLoader
          show={salesmanLoading}
          message="Loading dashboard data..."
          variant="primary"
          blurIntensity="md"
        />
        {salesmanError ? (
          <div className="max-w-full mx-auto px-4">
            <p className="text-center text-red-500 py-8">{salesmanError}</p>
          </div>
        ) : (
          <SalesmanDashboard data={salesmanDashboardData} />
        )}
      </DashboardLayout>
    );
  }

  if (roleId === "4") return <CustomerDashboard />;

  // ─── Card config per order type ────────────────────────────────────────────
  const getCardsForType = (type) => {
    switch (type) {
      case "cashback":
        return [
          {
            icon: <FiDollarSign />,
            title: "Total Amount",
            value: `₹${totals.overall_amount}`,
            color: "text-amber-500",
          },
          {
            icon: <FiTag />,
            title: "Total Generated QR",
            value: totals.overall_quantity,
            color: "text-blue-600",
          },
          {
            icon: <FiCheckCircle />,
            title: "Active QRs",
            value: totals.overall_active,
            color: "text-green-500",
          },
          {
            icon: <FiXCircle />,
            title: "Inactive QRs",
            value: totals.overall_inactive,
            color: "text-gray-500",
          },
          {
            icon: <FiUserCheck />,
            title: "Used QRs",
            value: totals.overall_used,
            color: "text-indigo-500",
          },
          {
            icon: <FiClock />,
            title: "Expired QRs",
            value: totals.overall_expired,
            color: "text-red-500",
          },
        ];

      case "loyality":
        return [
          {
            icon: <FiTag />,
            title: "Total Generated QR",
            value: totals.overall_quantity,
            color: "text-blue-600",
          },
          {
            icon: <FiCheckCircle />,
            title: "Active QRs",
            value: totals.overall_active,
            color: "text-green-500",
          },
          {
            icon: <FiXCircle />,
            title: "Inactive QRs",
            value: totals.overall_inactive,
            color: "text-gray-500",
          },
          {
            icon: <FiUserCheck />,
            title: "Used QRs",
            value: totals.overall_used,
            color: "text-indigo-500",
          },
          {
            icon: <FiClock />,
            title: "Expired QRs",
            value: totals.overall_expired,
            color: "text-red-500",
          },
          {
            icon: <FiLayers />,
            title: "Total Pending",
            value: totals.overall_pending,
            color: "text-yellow-500",
          },
          // {
          //   icon: <FiLayers />,
          //   title: "Total Accumulated",
          //   value: totals.overall_accumulated,
          //   color: "text-purple-500",
          //   navigateTo: "/dashboard/redeemedstatistcis",
          // },
          {
            icon: <FiLayers />,
            title: "Total Redeemed",
            value: totals.overall_redeemed,
            color: "text-pink-500",
            navigateTo: "/dashboard/redeemedstatistcis/summary/redeemed",
          },
        ];

      case "promotional":
        return [
          // {
          //   icon: <FiLayers />,
          //   title: "Total Schemes",
          //   value: totals.overall_schemes,
          //   color: "text-purple-500",
          // },
          {
            icon: <FiTag />,
            title: "Total Generated QR",
            value: totals.overall_quantity,
            color: "text-blue-600",
          },
          {
            icon: <FiCheckCircle />,
            title: "Active QRs",
            value: totals.overall_active,
            color: "text-green-500",
          },
          {
            icon: <FiXCircle />,
            title: "Inactive QRs",
            value: totals.overall_inactive,
            color: "text-gray-500",
          },
          {
            icon: <FiUserCheck />,
            title: "Used QRs",
            value: totals.overall_used,
            color: "text-indigo-500",
          },
          {
            icon: <FiClock />,
            title: "Expired QRs",
            value: totals.overall_expired,
            color: "text-red-500",
          },
        ];

      default:
        return [];
    }
  };

  const cards = getCardsForType(selectedOrderType);

  // ── Pie chart footer varies by type ────────────────────────────────────────
  const renderPieFooter = () => {
    switch (selectedOrderType) {
      case "cashback":
        return (
          <>
            <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <span className="inline-block w-3 h-3 rounded-full bg-[#004D99]"></span>
              Total Amount: ₹{totals.overall_amount}
            </span>
            <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <span className="inline-block w-3 h-3 rounded-full bg-[#4394E5]"></span>
              Total QR: {totals.overall_quantity}
            </span>
          </>
        );
      case "loyality":
      case "promotional":
        return (
          <>
            <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <span className="inline-block w-3 h-3 rounded-full bg-[#818CF8]"></span>
              Total Schemes: {totals.overall_schemes}
            </span>
            <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <span className="inline-block w-3 h-3 rounded-full bg-[#4394E5]"></span>
              Total QR: {totals.overall_quantity}
            </span>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <DashboardLayout>
      <FullScreenLoader
        show={loading}
        message="Loading dashboard statistics..."
        variant="primary"
        blurIntensity="md"
      />
      <div className="max-w-full mx-auto px-4">
        {/* ORDER TYPE FILTER TABS — role 6 only */}
        {isMultiTypeRole && availableOrderTypes.length > 0 && (
          <div className="mt-6 mb-6 bg-white rounded-lg shadow-md p-4 border-l-4 border-amber-500">
            <div className="flex items-center justify-between gap-6 flex-wrap">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Filter by Order Type
                </label>
                <div className="flex gap-3 flex-wrap">
                  {availableOrderTypes.map((type) => (
                    <button
                      key={type}
                      onClick={() => setSelectedOrderType(type)}
                      className={`px-6 py-2 rounded-lg font-medium transition-all capitalize ${
                        selectedOrderType === type
                          ? "bg-amber-500 text-white shadow-md"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                    >
                      {getOrderTypeLabel(type)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Showing data for:</p>
                <p className="text-lg font-bold text-amber-600">
                  {getOrderTypeLabel(selectedOrderType)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* VIEWING BADGE — non role-6 */}
        {!isMultiTypeRole && selectedOrderType && (
          <div className="mt-6 mb-4 flex items-center gap-2">
            <span className="text-sm text-gray-500">Viewing:</span>
            <span className="bg-amber-100 text-amber-700 text-sm font-semibold px-3 py-1 rounded-full">
              {getOrderTypeLabel(selectedOrderType)}
            </span>
          </div>
        )}

        {error ? (
          <p className="text-center text-red-500">{error}</p>
        ) : (
          <>
            {/* Summary Cards — dynamic per order type */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
              {cards.map((card, index) => (
                <DashboardCard
                  key={index}
                  icon={card.icon}
                  title={card.title}
                  value={card.value}
                  color={card.color}
                  onClick={() =>
                    navigate(card.navigateTo || "/dashboard/CouponStat")
                  }
                />
              ))}
            </div>

            {/* Charts */}
            {totals.overall_quantity > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                {/* Pie Chart */}
                <div className="bg-white p-6 rounded-2xl shadow-md border border-amber-500 hover:shadow-lg transition-shadow duration-300">
                  <h6 className="text-lg font-bold text-gray-950 mb-4 text-center">
                    QR Status Overview —{" "}
                    <span className="text-amber-500">
                      {getOrderTypeLabel(selectedOrderType)}
                    </span>
                  </h6>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label
                      >
                        {pieData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 text-center flex items-center justify-center gap-6 flex-wrap">
                    {renderPieFooter()}
                  </div>
                </div>

                {/* Bar Chart */}
                {selectedOrderType !== "loyality" && (
                  <div className="bg-white p-6 rounded-2xl shadow-md border border-amber-500 hover:shadow-lg transition-shadow duration-300">
                    <h6 className="text-lg font-bold text-gray-950 mb-4 text-center">
                      State & City Count Overview
                    </h6>

                    {stateStats.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
                        <p className="font-semibold">No redemption data yet</p>
                        <p className="text-gray-900 text-md max-w-md">
                          State and city statistics will appear here once QRs
                          are scanned and redeemed.
                        </p>
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={320}>
                        <BarChart
                          data={stateStats.map((item) => ({
                            name: item.state,
                            city_count: item.city_count,
                          }))}
                          margin={{ top: 20, right: 30, left: 0, bottom: 50 }}
                          barCategoryGap={20}
                          barGap={6}
                        >
                          <XAxis
                            dataKey="name"
                            tick={{ fontSize: 12, fill: "#000000" }}
                            axisLine={{ stroke: "#F59E0B" }}
                            interval={0}
                            angle={-45}
                            textAnchor="end"
                            height={70}
                          />
                          <YAxis
                            allowDecimals={false}
                            tick={{ fontSize: 12, fill: "#000000" }}
                            axisLine={{ stroke: "#F59E0B" }}
                            tickCount={6}
                            domain={[0, "dataMax + 2"]}
                          />
                          <Tooltip
                            content={({ active, payload, label }) => {
                              if (active && payload && payload.length) {
                                const cities = cityStats
                                  .filter((c) => c.state === label)
                                  .map((c) => (
                                    <li
                                      key={c.city}
                                      className="text-gray-600 text-sm"
                                    >
                                      <span className="font-medium text-gray-800">
                                        {c.city}
                                      </span>
                                      :{" "}
                                      <span className="text-indigo-600 font-semibold">
                                        {c.total_redemptions}
                                      </span>
                                    </li>
                                  ));
                                return (
                                  <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-xl max-w-xs space-y-2">
                                    <p className="text-base font-semibold text-gray-950">
                                      {label}
                                    </p>
                                    <p className="text-sm text-gray-700">
                                      <span className="font-medium">
                                        City Count:
                                      </span>{" "}
                                      <span className="text-gray-950 font-semibold">
                                        {payload[0].value}
                                      </span>
                                    </p>
                                    <div className="space-y-1">
                                      <p className="text-sm font-semibold text-gray-600">
                                        Cities:
                                      </p>
                                      <ul className="list-disc list-inside pl-2">
                                        {cities}
                                      </ul>
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Bar
                            dataKey="city_count"
                            fill="url(#colorAmber)"
                            barSize={14}
                            radius={[8, 8, 0, 0]}
                            name="City Count"
                          />
                          <defs>
                            <linearGradient
                              id="colorAmber"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="0%"
                                stopColor="#F59E0B"
                                stopOpacity={0.9}
                              />
                              <stop
                                offset="100%"
                                stopColor="#FBBF24"
                                stopOpacity={0.7}
                              />
                            </linearGradient>
                          </defs>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                )}
              </div>
            ) : (
              !loading && (
                <div>
                  {/* <h2 className="text-xl font-bold text-gray-800 mb-2">
                    Welcome to the {getOrderTypeLabel(selectedOrderType)} Dashboard
                  </h2>
                  <p className="mb-4">
                    Start by creating your first coupon to view QR statistics.
                  </p>
                  <button
                    onClick={() => navigate("/dashboard/orderRequest")}
                    className="inline-block bg-amber-500 hover:bg-amber-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                  >
                    Click here to Create Coupon
                  </button> */}
                </div>
              )
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default OrderStatsDashboard;
